import { Shield, Sprout } from "lucide-react";

const SECTIONS = [
  {
    id: "who-we-are",
    title: "1. Who we are",
    body: `Matt's Daily Savers ("the site", "we", "us") is a UK-based personal finance blog and directory operated by Matt as a sole trader. This Privacy Policy explains what personal information we collect, why we collect it, and how we handle it, in line with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.

If you have any questions about this policy or how your data is handled, please use the Contact page.`,
  },
  {
    id: "what-we-collect",
    title: "2. What personal data we collect",
    body: `We only collect the minimum information needed to run the site:

• Contact form submissions: your name, email address and the content of your message.
• Automatic technical data: your IP address, device type, browser type, approximate location (country/region), pages visited, and referring URL, collected via server logs and analytics.
• Cookies and similar technologies: see the "Cookies" section below.

We do not run an email newsletter and do not collect email addresses for marketing purposes.

We do not knowingly collect data from children under 13. If you believe a child has provided us with personal data, please contact us and we'll delete it.`,
  },
  {
    id: "legal-basis",
    title: "3. Legal bases for processing",
    body: `Under UK GDPR we rely on the following legal bases:

• Consent — for optional cookies and personalised ads.
• Legitimate interests — to keep the site secure, prevent fraud, understand how visitors use the site, and improve the content.
• Legal obligation — to comply with UK law where required (e.g. responding to a lawful request).`,
  },
  {
    id: "advertising",
    title: "4. Advertising — Google AdSense and third-party vendors",
    body: `This site uses Google AdSense, a third-party advertising service provided by Google Ireland Limited, and may work with additional third-party ad networks and vendors. Ads help fund the running of the site.

You should be aware that:

• Google, as a third-party vendor, uses cookies (including the DoubleClick DART cookie) to serve ads based on your prior visits to this site and to other websites on the internet.
• Third-party vendors and ad networks may also use cookies, web beacons, pixels and similar technologies to gather information about your visits in order to provide advertisements about goods and services that may be of interest to you.
• Ads served may use non-personally-identifiable information (such as browser type, time of visit, pages viewed, and general location) to deliver relevant advertisements.
• We do not have access to or control over the cookies used by third-party advertisers.

You can opt out of personalised advertising served by Google by visiting Google Ads Settings (https://adssettings.google.com). You can opt out of some third-party vendors' use of cookies for personalised advertising by visiting www.aboutads.info or www.youronlinechoices.eu.

For more detail on how Google uses information from sites that use its services, see https://policies.google.com/technologies/partner-sites.`,
  },
  {
    id: "cookies",
    title: "5. Cookies and tracking technologies",
    body: `Cookies are small text files stored on your device. This site (and its partners) may set the following categories of cookies:

• Strictly necessary cookies — required for the site to function (e.g. keeping you signed in to the admin area). These cannot be switched off.
• Analytics cookies — help us understand which pages are popular so we can improve the content.
• Advertising cookies — used by Google AdSense and other ad partners to serve and measure ads, including personalised ads based on your interests.
• Affiliate / referral tracking cookies — when you click a referral link on this site (e.g. to a cashback site, reward app, card provider or survey panel), the destination service typically sets a cookie so it can attribute the sign-up or purchase back to us. This is how we earn a small commission at no extra cost to you.

You can control or delete cookies through your browser settings. Blocking all cookies may prevent parts of the site from working correctly, and may prevent referral tracking (in which case we will not receive any commission if you sign up).`,
  },
  {
    id: "contact",
    title: "6. Contact form",
    body: `If you send us a message through the Contact page, we retain your name, email address and message so we can reply and, where necessary, keep a record of any ongoing correspondence. Legal basis: our legitimate interest in responding to you. We keep this data no longer than is reasonably necessary.`,
  },
  {
    id: "retention",
    title: "7. How long we keep data",
    body: `We keep personal data only as long as necessary for the purpose it was collected for:

• Contact submissions: up to 24 months.
• Server and analytics logs: typically up to 14 months.

We may keep data longer if required by law.`,
  },
  {
    id: "your-rights",
    title: "8. Your rights",
    body: `Under UK GDPR you have the right to:

• Access the personal data we hold about you.
• Ask us to correct data that is inaccurate or incomplete.
• Ask us to delete your personal data ("right to be forgotten").
• Restrict or object to how we process your data.
• Withdraw consent at any time (where processing is based on consent).
• Lodge a complaint with the Information Commissioner's Office (ICO) at ico.org.uk.

To exercise any of these rights, please use the Contact page.`,
  },
  {
    id: "changes",
    title: "9. Changes to this policy",
    body: `We may update this Privacy Policy from time to time. The "Last updated" date at the top of this page will always show the most recent version.`,
  },
];

export default function Privacy() {
  const updated = "December 2025";
  return (
    <main data-testid="privacy-page">
      <section className="mds-container pt-14 pb-6">
        <span className="mds-badge mds-badge-primary"><Shield size={14} /> Legal</span>
        <h1 className="font-heading font-semibold text-4xl sm:text-5xl mt-4 tracking-tight leading-tight">Privacy Policy</h1>
        <p className="text-[#4A5D54] mt-3">Last updated: {updated}</p>
      </section>

      <section className="mds-container pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-4 lg:sticky lg:top-24 self-start hidden lg:block">
          <div className="mds-card">
            <div className="flex items-center gap-2 mb-4">
              <Sprout size={18} className="text-[#6B8F73]" />
              <h3 className="font-heading font-semibold">On this page</h3>
            </div>
            <ul className="space-y-2 text-sm">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="mds-link">{s.title}</a>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <article className="lg:col-span-8 space-y-8 mds-prose text-base">
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-24" data-testid={`privacy-${s.id}`}>
              <h2 className="font-heading font-semibold text-2xl text-[#1C2321] mb-3">{s.title}</h2>
              <div className="whitespace-pre-line">{s.body}</div>
            </section>
          ))}
        </article>
      </section>
    </main>
  );
}
